import React, { useRef, useState } from 'react';
import { ChevronDown, Download, RefreshCw } from 'lucide-react';
import { useKeepInViewport } from '../hooks/useKeepInViewport';

export interface DownloadItem {
  key: string;
  title: string;
  /** One line under the title: what the file holds. */
  hint?: string;
  disabled?: boolean;
  onSelect: () => void;
}

interface Props {
  items: DownloadItem[];
  /** A file is being prepared: the button shows it and cannot be pressed again. */
  busy?: boolean;
  disabled?: boolean;
  label?: string;
}

// One Download button for the report pages: it lists what can be downloaded, with a line on what each
// file holds. Escape and a click outside close it; the arrow keys move between the entries, and focus goes back
// to the button when it closes.
export default function DownloadMenu({ items, busy = false, disabled = false, label = 'Download' }: Props) {
  const [open, setOpen] = useState(false);
  const menuRef = useKeepInViewport(open); // stays inside a narrow screen
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  // Up, Down, Home and End move between the entries that can be chosen; Tab leaves the menu and closes it.
  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close(true);
      return;
    }
    if (e.key === 'Tab') {
      close(false);
      return;
    }
    const entries = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)'));
    if (entries.length === 0) return;
    const at = entries.indexOf(document.activeElement as HTMLButtonElement);
    const target =
      e.key === 'ArrowDown' ? entries[(at + 1) % entries.length]
      : e.key === 'ArrowUp' ? entries[(at - 1 + entries.length) % entries.length]
      : e.key === 'Home' ? entries[0]
      : e.key === 'End' ? entries[entries.length - 1]
      : null;
    if (target) {
      e.preventDefault();
      target.focus();
    }
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy || disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-50 active:scale-[0.98] disabled:opacity-50 motion-reduce:transition-none motion-reduce:active:scale-100"
      >
        {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        {busy ? 'Preparing…' : label}
        <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
      </button>
      {open && (
        <>
          <div role="presentation" className="fixed inset-0 z-40" onClick={() => close(false)} />
          <div
            ref={menuRef}
            role="menu"
            tabIndex={-1}
            onKeyDown={onMenuKeyDown}
            className="absolute right-0 z-50 mt-1 w-72 max-w-[calc(100vw-1rem)] overflow-hidden focus:outline-none rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
          >
            {items.map((item, i) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                autoFocus={i === 0}
                disabled={item.disabled}
                onClick={() => {
                  close(true);
                  item.onSelect();
                }}
                className="block w-full px-3 py-2 text-left hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none disabled:opacity-50"
              >
                <span className="block text-sm font-medium text-gray-800">{item.title}</span>
                {item.hint && <span className="block text-xs text-gray-500">{item.hint}</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
